import React from 'react';
import ReactPlayer from 'react-player';

import styled from 'styled-components';

import NwIcon from '@components/Gui/NwIcon';
import {
    GALLERY_MEDIA_SOURCE,
    GALLERY_MEDIA_TYPE,
} from '@containers/Art/Galleries/Galleries.constants';
import {
    getArtDocumentDisplayName,
    getArtStoredMediaType,
    getArtStoredMediaUrl,
} from '@containers/Art/ArtDocuments';
import useArtDocument from '@containers/Art/useArtDocument';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const getEmbedProviderIcon = (provider) => {
    if (provider === 'youtube') return byPrefixAndName.fab['youtube'];
    if (provider === 'vimeo') return byPrefixAndName.fab['vimeo'];
    return byPrefixAndName.fass['play'];
};

const EncodingVideoPreview = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.65);
    color: #fff;
    text-align: center;
    padding: 0.5rem;

    h1 {
        font-size: 1rem;
        margin: 0;
        font-weight: 500;
        line-height: 1.35;
        color: #fff;
    }

    small {
        font-size: ${(props) => props.theme.FontSizeSm};
        text-transform: uppercase;
        color: #fff;
        margin-bottom: 0.75rem;
    }
`;

const ArtDocumentMediaPreview = ({
    documentId,
    fallbackIcon = byPrefixAndName.fasl['image'],
    fallbackLabel = 'Media',
    isUploading = false,
    media,
    posterOnly = false,
    showControls = false,
    videoClassName = 'media-video-player',
    videoPosterClassName = 'media-video-poster',
    videoPosterIconClassName = 'media-video-icon',
}) => {
    const useStoredMedia = Boolean(media);
    const storedMediaType = getArtStoredMediaType(media);
    const storedMediaUrl = getArtStoredMediaUrl(media);
    const storedMediaSource = media?.Source || media?.source;
    const documentLookupId = useStoredMedia ? undefined : documentId;
    const { document, embedProvider, isEncodingVideo, loading, mediaType, playbackUrl, previewUrl, source } =
        useArtDocument(documentLookupId);
    const resolvedMediaType = useStoredMedia ? storedMediaType : mediaType;
    const resolvedPlaybackUrl =
        useStoredMedia && resolvedMediaType === GALLERY_MEDIA_TYPE.VIDEO ? storedMediaUrl : playbackUrl;
    const resolvedPreviewUrl =
        useStoredMedia && resolvedMediaType === GALLERY_MEDIA_TYPE.IMAGE ? storedMediaUrl : previewUrl;
    const resolvedSource = useStoredMedia ? storedMediaSource : source;
    const title = getArtDocumentDisplayName(document, fallbackLabel);

    if (isUploading) {
        return <NwIcon icon={byPrefixAndName.fasl['spinner-third']} spin />;
    }

    if (useStoredMedia && (!storedMediaUrl || !storedMediaType)) {
        return <NwIcon icon={fallbackIcon} />;
    }

    if (!documentLookupId && !storedMediaUrl) {
        return <NwIcon icon={fallbackIcon} />;
    }

    if (loading && !useStoredMedia) {
        return <NwIcon icon={byPrefixAndName.fasl['spinner-third']} spin />;
    }

    if (resolvedMediaType === GALLERY_MEDIA_TYPE.VIDEO) {
        if (isEncodingVideo && !useStoredMedia) {
            return (
                <EncodingVideoPreview>
                    <h1>Encoding video</h1>
                    <small>Please wait...</small>
                    <NwIcon icon={byPrefixAndName.fasl['camera-movie']} beat />
                </EncodingVideoPreview>
            );
        }

        if (resolvedSource === GALLERY_MEDIA_SOURCE.UPLOAD && resolvedPlaybackUrl && !embedProvider) {
            return (
                <video
                    controls={showControls}
                    muted={!showControls}
                    playsInline
                    preload="metadata"
                    src={resolvedPlaybackUrl}
                    title={title}
                />
            );
        }

        const isEmbedVideo = resolvedSource === GALLERY_MEDIA_SOURCE.EMBED || Boolean(embedProvider);

        if (posterOnly && isEmbedVideo) {
            if (resolvedPreviewUrl) {
                return (
                    <div className={videoPosterClassName}>
                        <img alt={title} src={resolvedPreviewUrl} />
                        <span className={videoPosterIconClassName}>
                            <NwIcon icon={getEmbedProviderIcon(embedProvider)} />
                        </span>
                    </div>
                );
            }

            return <NwIcon icon={getEmbedProviderIcon(embedProvider)} />;
        }

        if (resolvedPlaybackUrl) {
            return (
                <div className={videoClassName}>
                    <ReactPlayer
                        controls={showControls}
                        height="100%"
                        light={resolvedPreviewUrl || false}
                        muted={!showControls}
                        url={resolvedPlaybackUrl}
                        width="100%"
                    />
                </div>
            );
        }

        if (resolvedPreviewUrl) {
            return (
                <div className={videoPosterClassName}>
                    <img alt={title} src={resolvedPreviewUrl} />
                    <span className={videoPosterIconClassName}>
                        <NwIcon icon={getEmbedProviderIcon(embedProvider)} />
                    </span>
                </div>
            );
        }

        return <NwIcon icon={byPrefixAndName.fasl['film']} />;
    }

    return resolvedPreviewUrl ? <img src={resolvedPreviewUrl} alt={title} /> : <NwIcon icon={fallbackIcon} />;
};

export default ArtDocumentMediaPreview;
